import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Callout,
  DataListItem,
  QRCode,
  Radio,
  RadioOption,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useWalletContext } from 'hooks/context'
import { usePrepareNewlyCreatedCryptoWallet } from 'pages/WalletDetails/hooks/usePrepareNewlyCreatedCryptoWallet'
import isEmpty from 'lodash-es/isEmpty'

export const CryptoExtendedDetails = () => {
  const { t } = useTranslation()
  const wallet = useWalletContext()
  const [protocol, setProtocol] = useState<string>(wallet?.protocol || '')
  const { isError, isPending } = usePrepareNewlyCreatedCryptoWallet()

  const protocolItems = useMemo<RadioOption[]>(() => {
    return [
      { label: wallet?.protocol, value: wallet?.protocol || '' },
      ...(wallet?.alternatives?.map(p => ({ label: p?.protocol, value: p?.protocol || '' })) || []),
    ]
  }, [wallet])

  const address = useMemo(() => {
    if (protocol === wallet?.protocol) {
      return wallet?.address || ''
    }
    return wallet?.alternatives?.find(p => p?.protocol === protocol)?.address || ''
  }, [wallet, protocol])

  const network = useMemo(() => {
    return wallet?.currency?.protocols?.find(p => p?.code === protocol)?.network || ''
  }, [wallet?.currency?.protocols, protocol])

  if (isPending) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }
  if (isError) {
    return <Callout variant="error">{t('oopsSomethingWentWrongFetchDespoitDetails')}</Callout>
  }

  return (
    <Box data-testid="crypto-extended-details">
      <Box flex dir="row" gap={12} paddingR={12} paddingB={12}>
        <QRCode value={address || ''} />
        <Box {...(isEmpty(wallet?.alternatives) ? {} : { paddingT: 12 })}>
          {!isEmpty(wallet?.alternatives) && (
            <Box width="full" paddingB={28} data-testid="protocol-radio-button-group">
              <Radio
                testId="protocol"
                onChange={setProtocol}
                options={protocolItems}
                value={protocol}
                border={true}
              />
            </Box>
          )}
          <Box>
            <DataListItem
              id="wallet-details-address"
              title={t('yourAddress', {
                currencyCode: wallet.currency?.code?.toUpperCase(),
                protocol: network ? `(${network} network)` : '',
              })}
              body={ellipseMiddleText(address, 12, 12)}
              copyText={address}
            />
            {wallet?.lookup && (
              <DataListItem
                id="wallet-details-destination-tag"
                title={t('destinationTag')}
                body={ellipseMiddleText(wallet?.lookup, 12, 12)}
                copyText={wallet?.lookup}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Callout testId="crypto-send-warning" variant="warning">
        {t('cryptoDepositWarning', {
          currencyCode: wallet.currency?.code?.toUpperCase(),
          protocol: network ? `(${network} network)` : '',
        })}
      </Callout>
    </Box>
  )
}
